export function applySelectionRewrite(article, selectedText, suggestion, context = null) {
  const replacementParagraphs = splitParagraphs(suggestion);
  if (replacementParagraphs.length === 0) return article;

  if (context?.target === "title") {
    return { ...article, title: replacementParagraphs.join("\n\n") };
  }

  if (context?.target === "summary") {
    return { ...article, summary: replacementParagraphs.join("\n\n") };
  }

  if (context?.target === "section" && context.sectionId) {
    const next = replaceSectionParagraphRange(article, context, replacementParagraphs, selectedText);
    if (next !== article) return next;
  }

  return replaceSelectionByTextSearch(article, selectedText, replacementParagraphs);
}

export function applyReviewIssueRewrite(article, issue, rewrittenText) {
  const paragraphs = splitParagraphs(rewrittenText);
  if (!issue?.targetSectionId || paragraphs.length === 0) return article;

  let changed = false;
  const sections = (article.sections || []).map((section) => {
    if (section.id !== issue.targetSectionId) return section;
    changed = true;
    return { ...section, paragraphs };
  });

  return changed ? { ...article, sections } : article;
}

export function applyImagePlaceholder(article, image) {
  const sections = article.sections || [];
  if (sections.length === 0) return article;

  const placeholder = createImagePlaceholder(image);
  const targetIndex = findImageTargetSectionIndex(sections, image);
  const insertAtStart = /封面|首图|开头/.test(image?.placement || "");
  let changed = false;

  const nextSections = sections.map((section, index) => {
    if (index !== targetIndex) return section;
    if (section.paragraphs.some((paragraph) => paragraph.includes(placeholder))) return section;
    changed = true;
    const paragraphs = insertAtStart
      ? [placeholder, ...section.paragraphs]
      : [...section.paragraphs, placeholder];
    return { ...section, paragraphs };
  });

  return changed ? { ...article, sections: nextSections } : article;
}

export function buildFallbackImageSuggestions(article) {
  if (article?.imageSuggestions?.length) return article.imageSuggestions;

  const title = article?.title || "公众号文章";
  const sections = article?.sections || [];
  const sectionSuggestions = sections.slice(0, 2).map((section, index) => ({
    id: `img-sec-${index + 1}`,
    title: `${section.heading || `小节 ${index + 1}`}配图`,
    description: "用一张信息图或场景图承接这一节的核心观点，降低长文阅读负担。",
    placement: `${section.heading || `小节 ${index + 1}`}小节`
  }));

  return [
    {
      id: "img-cover",
      title: `${title}封面图`,
      description: "突出文章主题和读者收益，适合作为公众号首图或封面。",
      placement: "封面"
    },
    ...sectionSuggestions
  ];
}

export function ensureArticleImageSuggestions(article) {
  const imageSuggestions = buildFallbackImageSuggestions(article);
  return imageSuggestions === article.imageSuggestions
    ? article
    : { ...article, imageSuggestions };
}

export function buildPublishReminders({ article, review } = {}) {
  const issues = review?.issues || [];
  const highPriorityCount = issues.filter((issue) => issue.severity === "high").length;
  const sourceWarnings = article?.sourceWarnings || [];
  const imageSuggestions = article?.imageSuggestions || [];
  const hasImagePlaceholder = (article?.sections || []).some((section) =>
    section.paragraphs.some((paragraph) => paragraph.includes("![图片占位："))
  );

  const reminders = [];
  if (highPriorityCount > 0) {
    reminders.push(`有 ${highPriorityCount} 条高优先级检查建议，发布前建议先处理。`);
  } else if (issues.length > 0) {
    reminders.push(`还有 ${issues.length} 条检查建议，发布前建议逐条确认。`);
  } else {
    reminders.push("检查未发现明显问题，发布前仍建议通读标题、摘要和正文。");
  }

  for (const warning of sourceWarnings) {
    reminders.push(warning);
  }

  if (imageSuggestions.length > 0 && !hasImagePlaceholder) {
    reminders.push(`有 ${imageSuggestions.length} 条配图建议，可先插入配图占位符再导出。`);
  }

  return reminders;
}

function replaceSectionParagraphRange(article, context, replacementParagraphs, selectedText) {
  const { sectionId, startIndex, endIndex } = context;
  let changed = false;

  const sections = (article.sections || []).map((section) => {
    if (section.id !== sectionId) return section;

    const paragraphs = [...section.paragraphs];
    if (startIndex < 0 || startIndex >= paragraphs.length) return section;

    if (startIndex === endIndex) {
      const needle = normalizeSelectionText(selectedText);
      const paragraph = paragraphs[startIndex];
      if (!needle || !paragraph.includes(needle)) return section;
      paragraphs.splice(startIndex, 1, ...splitParagraphs(paragraph.replace(needle, replacementParagraphs.join("\n\n"))));
    } else {
      if (endIndex >= paragraphs.length || startIndex > endIndex) return section;
      paragraphs.splice(startIndex, endIndex - startIndex + 1, ...replacementParagraphs);
    }

    changed = true;
    return { ...section, paragraphs };
  });

  return changed ? { ...article, sections } : article;
}

function replaceSelectionByTextSearch(article, selectedText, replacementParagraphs) {
  const needle = normalizeSelectionText(selectedText);
  if (!needle) return article;

  let changed = false;
  const sections = (article.sections || []).map((section) => {
    for (let index = 0; index < section.paragraphs.length; index += 1) {
      const paragraph = section.paragraphs[index];
      if (!paragraph.includes(needle)) continue;

      changed = true;
      const paragraphs = [...section.paragraphs];
      paragraphs.splice(index, 1, ...splitParagraphs(paragraph.replace(needle, replacementParagraphs.join("\n\n"))));
      return { ...section, paragraphs };
    }

    const joined = section.paragraphs.join("\n\n");
    if (!joined.includes(needle)) return section;

    changed = true;
    return {
      ...section,
      paragraphs: splitParagraphs(joined.replace(needle, replacementParagraphs.join("\n\n")))
    };
  });

  return changed ? { ...article, sections } : article;
}

function normalizeSelectionText(text) {
  return String(text || "").replace(/\r\n/g, "\n").trim();
}

function splitParagraphs(text) {
  return String(text || "")
    .split(/\n{2,}|\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function createImagePlaceholder(image) {
  const title = image?.title || "配图建议";
  const placement = image?.placement || "正文中";
  const description = image?.description || "根据正文内容补充配图。";
  return `![图片占位：${title}]() ${placement}：${description}`;
}

function findImageTargetSectionIndex(sections, image) {
  const placement = image?.placement || "";
  const exactIndex = sections.findIndex((section) => placement.includes(section.heading));
  if (exactIndex !== -1) return exactIndex;

  const fuzzyIndex = sections.findIndex((section) => placement.includes(section.heading.slice(0, 2)));
  if (fuzzyIndex !== -1) return fuzzyIndex;

  return 0;
}
